import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Copyright from '../components/Copyright';
import NavBar from '../components/NavBar';
import Slideshow from '../components/Slideshow';
import { useEventList } from '../contexts/EventListProvider';

const formatDate = (iso: string): string => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    });
};

const getYoutubeEmbedUrl = (url: string): string => {
    const id = url.split('/').pop();
    return `https://www.youtube.com/embed/${id}`;
};

const EventDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { eventList, loading } = useEventList();
    const event = eventList.find(e => e.id === Number(id));

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <NavBar />
            <Box sx={{ pt: 14, pb: 8, px: { xs: 3, md: 8, lg: 12 } }}>

                {/* Back link */}
                <Typography
                    component={RouterLink}
                    to="/events"
                    data-testid="back-link"
                    variant="body2"
                    sx={{
                        color: '#9e9e9e',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.8rem',
                        letterSpacing: '0.08em',
                        textDecoration: 'none',
                        textTransform: 'uppercase',
                        display: 'inline-block',
                        mb: 5,
                        '&:hover': { color: '#f0ede8' },
                    }}
                >
                    ← Events
                </Typography>

                {loading ? null : !event ? (
                    <Typography sx={{ color: '#9e9e9e' }}>Event not found.</Typography>
                ) : (
                    <>
                        {/* Header */}
                        <Box sx={{ mb: 6 }}>
                            <Box sx={{ width: 40, height: 2, backgroundColor: '#c9a84c', mb: 3 }} />
                            <Typography
                                data-testid="detail-title"
                                variant="h2"
                                sx={{ mb: 1, fontSize: { xs: '2rem', md: '3rem' } }}
                            >
                                {event.title}
                            </Typography>
                            <Typography
                                data-testid="detail-date"
                                variant="body2"
                                sx={{ color: '#c9a84c', fontFamily: '"Inter", sans-serif', mb: 0.5 }}
                            >
                                {formatDate(event.date)}
                                {event.endDate && event.endDate !== event.date
                                    ? ` – ${formatDate(event.endDate)}`
                                    : ''}
                            </Typography>
                            {event.location && (
                                <Typography
                                    data-testid="detail-location"
                                    variant="body2"
                                    sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif' }}
                                >
                                    {event.location}
                                </Typography>
                            )}
                        </Box>

                        {event.recap ? (
                            <>
                                {/* Summary */}
                                <Box sx={{ maxWidth: 800, mb: 8 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                        <Box sx={{ width: 32, height: 2, backgroundColor: '#c9a84c', mr: 2 }} />
                                        <Typography
                                            variant="overline"
                                            sx={{ color: '#c9a84c', letterSpacing: '0.2em' }}
                                        >
                                            Event Summary
                                        </Typography>
                                    </Box>
                                    <Typography
                                        data-testid="recap-summary"
                                        variant="body1"
                                        sx={{
                                            color: '#c8c4be',
                                            lineHeight: 1.9,
                                            fontFamily: '"Inter", sans-serif',
                                            fontSize: '1rem',
                                            whiteSpace: 'pre-line',
                                        }}
                                    >
                                        {event.recap.summary}
                                    </Typography>
                                </Box>

                                {/* Slideshow */}
                                <Box data-testid="recap-slideshow" sx={{ mb: 10 }}>
                                    <Slideshow images={event.recap.photos} />
                                </Box>

                                {/* Videos / Speeches */}
                                {event.recap.videos && event.recap.videos.length > 0 && (
                                    <Box sx={{ mb: 8 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
                                            <Box sx={{ width: 32, height: 2, backgroundColor: '#c9a84c', mr: 2 }} />
                                            <Typography
                                                variant="overline"
                                                sx={{ color: '#c9a84c', letterSpacing: '0.2em' }}
                                            >
                                                Speeches
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                                gap: 4
                                            }}
                                        >
                                            {event.recap.videos.map((video, i) => (
                                                <Box key={i}>
                                                    <Box sx={{ outline: '1px solid rgba(201,168,76,0.3)', outlineOffset: '6px', mb: 2 }}>
                                                        <Box
                                                            sx={{
                                                                position: 'relative',
                                                                width: '100%',
                                                                paddingTop: '56.25%', // 16:9
                                                                backgroundColor: '#000',
                                                            }}
                                                        >
                                                            <iframe
                                                                src={getYoutubeEmbedUrl(video.url)}
                                                                title={video.title}
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            color: '#9e9e9e',
                                                            fontFamily: '"Inter", sans-serif',
                                                            letterSpacing: '0.05em',
                                                            textAlign: 'center',
                                                            mt: 1.5
                                                        }}
                                                    >
                                                        {video.title}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Typography
                                data-testid="recap-placeholder"
                                variant="body2"
                                sx={{ color: '#9e9e9e', fontFamily: '"Inter", sans-serif' }}
                            >
                                Recap coming soon.
                            </Typography>
                        )}
                    </>
                )}
            </Box>
            <Copyright />
        </Box>
    );
};

export default EventDetailPage;
